// src/components/admin/UserManagement.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
	ChevronLeft,
	ChevronRight,
	Search,
	UserCheck,
	UserX,
	FileText,
	PlusCircle,
	Trash2,
	AlertTriangle,
	Edit2,
} from 'lucide-react';
import { AppContext } from '../../App';
import { usersAPI, agentsAPI, propertiesAPI, adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { getDisplayAddress } from '../../utils/displayName';

const getDisplayName = (agent) => {
	if (!agent?.user) return '—';
	const u = agent.user;
	const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
	return name || u.email || '—';
};

const AgentManagement = () => {
	const { navigate } = useContext(AppContext);
	const [agents, setAgents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedAgent, setSelectedAgent] = useState(null);
	const [bills, setBills] = useState([]);
	const [billsLoading, setBillsLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(null);
	const [showDisableConfirm, setShowDisableConfirm] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editForm, setEditForm] = useState({});
	const [editLoading, setEditLoading] = useState(false);

	useEffect(() => {
		fetchAgents();
	}, []);

	useEffect(() => {
		if (selectedAgent) {
			fetchBills(selectedAgent.id);
		} else {
			setBills([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAgent?.id]);

	const fetchAgents = async () => {
		try {
			setLoading(true);
			const data = await agentsAPI.getAll();
			setAgents(Array.isArray(data) ? data : []);
		} catch {
			toast.error('Failed to load agents');
			setAgents([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchBills = async (agentId) => {
		if (!agentId) return;
		try {
			setBillsLoading(true);
			const data = await propertiesAPI.getAssignedToAgent(agentId);
			setBills(Array.isArray(data) ? data : []);
		} catch {
			toast.error('Failed to load bills');
			setBills([]);
		} finally {
			setBillsLoading(false);
		}
	};

	const filteredAgents = agents.filter(
		(a) =>
			getDisplayName(a).toLowerCase().includes(searchQuery.toLowerCase()) ||
			(a.user?.email && a.user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(a.user?.phone && a.user.phone.includes(searchQuery)),
	);

	const handleDisableAgent = async () => {
		if (!selectedAgent || actionLoading) return;
		setActionLoading('disable');
		try {
			await adminAPI.unassignAllFromAgent(selectedAgent.id);
			await usersAPI.update(selectedAgent.id, { isActive: false });
			toast.success('Agent disabled. All bills have been removed.');
			setShowDisableConfirm(false);
			setSelectedAgent((prev) => prev ? { ...prev, user: { ...prev.user, isActive: false } } : null);
			fetchAgents();
			fetchBills(selectedAgent.id);
		} catch (error) {
			toast.error(error.response?.data?.message || 'Failed to disable agent');
		} finally {
			setActionLoading(null);
		}
	};

	const handleEnableAgent = async () => {
		if (!selectedAgent || actionLoading) return;
		setActionLoading('enable');
		try {
			await usersAPI.update(selectedAgent.id, { isActive: true });
			toast.success('Agent enabled. They can log in again.');
			setSelectedAgent((prev) => prev ? { ...prev, user: { ...prev.user, isActive: true } } : null);
			fetchAgents();
		} catch {
			toast.error('Failed to enable agent');
		} finally {
			setActionLoading(null);
		}
	};

	const handleDeleteAgent = async () => {
		if (!selectedAgent || actionLoading) return;
		setActionLoading('delete');
		try {
			await adminAPI.deleteUser(selectedAgent.user?.id || selectedAgent.id);
			toast.success('Agent deleted permanently.');
			setShowDeleteConfirm(false);
			setSelectedAgent(null);
			fetchAgents();
		} catch (error) {
			toast.error(error.response?.data?.message || 'Failed to delete agent');
		} finally {
			setActionLoading(null);
		}
	};

	const handleOpenEdit = async () => {
  setEditLoading(true);
  try {
    const fresh = await adminAPI.getUser(selectedAgent.user?.id || selectedAgent.id);
    setEditForm({
      firstName: fresh.firstName || '',
      lastName: fresh.lastName || '',
      email: fresh.email || '',
      phone: fresh.phone || '',
      ghanaCard: fresh.ghanaCard || '',
      address: fresh.address || '',
      assembly: fresh.assembly || '',
      treatmentCategory: selectedAgent.treatmentCategory || 'TREATMENT_1',
    });
  } catch {
    toast.error('Failed to load agent details');
    return;
  } finally {
    setEditLoading(false);
  }
  setShowEditModal(true);
};

	const handleEditSubmit = async () => {
		if (editLoading) return;
		setEditLoading(true);
		try {
			await adminAPI.updateUser(selectedAgent.user?.id || selectedAgent.id, editForm);
			toast.success('Agent updated successfully.');
			setShowEditModal(false);
			fetchAgents();
			setSelectedAgent((prev) => prev ? {
				...prev,
				treatmentCategory: editForm.treatmentCategory,
				user: { ...prev.user, ...editForm },
			} : null);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to update agent');
		} finally {
			setEditLoading(false);
		}
	};

	const handleRemoveBill = async (propertyId) => {
		if (actionLoading) return;
		setActionLoading(propertyId);
		try {
			await propertiesAPI.unassign(propertyId);
			toast.success('Bill removed from agent');
			fetchBills(selectedAgent.id);
		} catch {
			toast.error('Failed to remove bill');
		} finally {
			setActionLoading(null);
		}
	};

	const isActive = selectedAgent?.user?.isActive !== false;

	if (selectedAgent) {
		return (
			<div className="h-full flex flex-col">
				<header className="p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10">
					<div className="flex items-center gap-4 mb-4">
						<button type="button" onClick={() => setSelectedAgent(null)} className="glass-card p-3 rounded-xl">
							<ChevronLeft className="w-5 h-5 text-white" />
						</button>
						<div className="flex-1">
							<h2 className="text-xl font-black uppercase text-white">{getDisplayName(selectedAgent)}</h2>
							<p className="text-xs text-white/60">
								{selectedAgent.user?.email}{selectedAgent.user?.phone && ` · ${selectedAgent.user.phone}`}
							</p>
							{!isActive && (
								<span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded bg-red-500/30 text-red-300">
									Disabled
								</span>
							)}
						</div>
					</div>
					<div className="flex gap-2 flex-wrap">
						{isActive ? (
							<button
								type="button"
								onClick={() => setShowDisableConfirm(true)}
								disabled={!!actionLoading}
								className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/40 text-sm font-bold disabled:opacity-60"
							>
								<UserX className="w-4 h-4" />
								Disable
							</button>
						) : (
							<button
								type="button"
								onClick={handleEnableAgent}
								disabled={!!actionLoading}
								className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 text-sm font-bold disabled:opacity-60"
							>
								<UserCheck className="w-4 h-4" />
								Enable
							</button>
						)}
						<button
							type="button"
							onClick={handleOpenEdit}
							disabled={!!actionLoading}
							className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/40 text-sm font-bold disabled:opacity-60"
						>
							<Edit2 className="w-4 h-4" />
							Edit
						</button>
						<button
							type="button"
							onClick={() => setShowDeleteConfirm(true)}
							disabled={!!actionLoading}
							className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/30 text-red-300 border border-red-700/40 text-sm font-bold disabled:opacity-60"
						>
							<Trash2 className="w-4 h-4" />
							Delete
						</button>
						<button
							type="button"
							onClick={() => navigate(`/admin/assign-properties/${selectedAgent.id}`)}
							className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFC107]/20 text-[#FFC107] border border-[#FFC107]/40 text-sm font-bold"
						>
							<PlusCircle className="w-4 h-4" />
							Add more bills
						</button>
					</div>
				</header>

				<main className="flex-1 overflow-y-auto p-6 pb-8 no-scrollbar">
					<div className="mb-6">
						<label className="block text-sm font-bold text-white/80 mb-2">Treatment</label>
						<select
							value={selectedAgent.treatmentCategory || 'TREATMENT_1'}
							onChange={async (e) => {
								const value = e.target.value;
								if (value !== 'TREATMENT_1' && value !== 'TREATMENT_2') return;
								if (actionLoading) return;
								setActionLoading('treatment');
								try {
									await adminAPI.updateAgentTreatment(selectedAgent.id, value);
									setSelectedAgent((prev) => prev ? { ...prev, treatmentCategory: value } : null);
									toast.success(value === 'TREATMENT_2' ? 'Treatment set to Full Info' : 'Treatment set to Limited Info');
								} catch (err) {
									toast.error(err.response?.data?.message || 'Failed to update treatment');
								} finally {
									setActionLoading(null);
								}
							}}
							disabled={!!actionLoading}
							className="w-full max-w-xs p-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white disabled:opacity-60"
							style={{ color: 'white' }}
						>
							<option value="TREATMENT_1" className="bg-[#001E3C] text-white">Treatment 1 (Limited Info)</option>
							<option value="TREATMENT_2" className="bg-[#001E3C] text-white">Treatment 2 (Full Info)</option>
						</select>
						<p className="text-xs text-white/60 mt-1">Determines what info the agent can see.</p>
					</div>

					<h3 className="text-sm font-bold text-white/80 mb-3 flex items-center gap-2">
						<FileText className="w-4 h-4" />
						Assigned bills ({bills.length})
					</h3>
					{billsLoading ? (
						<p className="text-white/50 text-sm">Loading bills...</p>
					) : bills.length === 0 ? (
						<p className="text-white/50 text-sm">No bills assigned. Use "Add more bills" to assign.</p>
					) : (
						<div className="space-y-3">
							{bills.map((bill) => (
								<div key={bill.id} className="glass-card p-4 border-l-4 border-[#FFC107] flex justify-between items-center gap-3">
									<div className="min-w-0 flex-1">
										<p className="font-mono font-bold text-white text-sm">{bill.valuationNo || bill.billNo}</p>
										<p className="text-xs text-white/60 truncate">{getDisplayAddress(bill)}</p>
										<p className="text-xs text-[#FFC107]">₵{(Number(bill.totalDue ?? bill.total_due) || 0).toLocaleString()}</p>
									</div>
									<button
										type="button"
										onClick={() => handleRemoveBill(bill.id)}
										disabled={!!actionLoading}
										className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 text-xs font-bold disabled:opacity-60"
									>
										<Trash2 className="w-4 h-4" />
										Remove
									</button>
								</div>
							))}
						</div>
					)}
				</main>

				{/* Edit modal */}
				{showEditModal && (
					<div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 overflow-y-auto">
						<div className="glass-card p-6 max-w-sm w-full border border-white/10 my-4">
							<div className="flex items-center justify-between mb-4">
								<h3 className="font-black text-white">Edit Agent</h3>
								<button type="button" onClick={() => setShowEditModal(false)} className="text-white/50 hover:text-white text-xl leading-none">✕</button>
							</div>
							<div className="space-y-3">
								{[
									{ label: 'First Name', key: 'firstName' },
									{ label: 'Last Name', key: 'lastName' },
									{ label: 'Email', key: 'email', type: 'email' },
									{ label: 'Phone', key: 'phone' },
									{ label: 'Ghana Card', key: 'ghanaCard' },
									{ label: 'Address', key: 'address' },
									{ label: 'Assembly', key: 'assembly' },
								].map(({ label, key, type = 'text' }) => (
									<div key={key}>
										<label className="block text-xs text-white/60 mb-1">{label}</label>
										<input
											type={type}
											value={editForm[key] || ''}
											onChange={(e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }))}
											className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none"
										/>
									</div>
								))}
								<div>
									<label className="block text-xs text-white/60 mb-1">Treatment</label>
									<select
										value={editForm.treatmentCategory || 'TREATMENT_1'}
										onChange={(e) => setEditForm((prev) => ({ ...prev, treatmentCategory: e.target.value }))}
										className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none"
										style={{ color: 'white' }}
									>
										<option value="TREATMENT_1" className="bg-[#001E3C]">Treatment 1 (Limited Info)</option>
										<option value="TREATMENT_2" className="bg-[#001E3C]">Treatment 2 (Full Info)</option>
									</select>
								</div>
							</div>
							<div className="flex gap-3 mt-5">
								<button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold">
									Cancel
								</button>
								<button type="button" onClick={handleEditSubmit} disabled={editLoading} className="flex-1 py-2 rounded-lg bg-blue-500 text-white text-sm font-bold disabled:opacity-60">
									{editLoading ? 'Saving…' : 'Save changes'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Delete confirmation modal */}
				{showDeleteConfirm && (
					<div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70">
						<div className="glass-card p-6 max-w-sm w-full border border-white/10">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
									<AlertTriangle className="w-6 h-6 text-red-400" />
								</div>
								<div>
									<h3 className="font-black text-white">Permanently delete agent?</h3>
									<p className="text-xs text-white/60">This cannot be undone.</p>
								</div>
							</div>
							<p className="text-sm text-white/80 mb-4">
								This will permanently delete <strong className="text-white">{getDisplayName(selectedAgent)}</strong> and all their data including assignments and permissions.
							</p>
							<div className="flex gap-3">
								<button type="button" onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold">
									Cancel
								</button>
								<button type="button" onClick={handleDeleteAgent} disabled={!!actionLoading} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-60">
									{actionLoading === 'delete' ? 'Deleting…' : 'Delete permanently'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Disable confirmation modal */}
				{showDisableConfirm && (
					<div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70">
						<div className="glass-card p-6 max-w-sm w-full border border-white/10">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
									<AlertTriangle className="w-6 h-6 text-red-400" />
								</div>
								<div>
									<h3 className="font-black text-white">Disable this agent?</h3>
									<p className="text-xs text-white/60">They will not be able to log in.</p>
								</div>
							</div>
							<p className="text-sm text-white/80 mb-4">
								All bills assigned to this agent will be removed. When they try to log in, they will see: &quot;Your account has been disabled.&quot;
							</p>
							<div className="flex gap-3">
								<button type="button" onClick={() => setShowDisableConfirm(false)} className="flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold">
									Cancel
								</button>
								<button type="button" onClick={handleDisableAgent} disabled={!!actionLoading} className="flex-1 py-2 rounded-lg bg-red-500 text-white text-sm font-bold disabled:opacity-60">
									{actionLoading === 'disable' ? 'Disabling…' : 'Disable'}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col">
			<header className="p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10">
				<div className="flex items-center gap-4 mb-4">
					<button
						type="button"
						onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}
						className="glass-card p-3 rounded-xl"
					>
						<ChevronLeft className="w-5 h-5 text-white" />
					</button>
					<h2 className="text-xl font-black uppercase text-white">Agent Management</h2>
					<p className="text-xs text-white/60 mt-1">Click an agent to manage bills, disable or enable</p>
				</div>
				<div className="relative">
					<Search className="absolute left-4 top-3.5 w-5 h-5 text-white/30" />
					<input
						type="text"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder-white/30"
						placeholder="Search agents by name, email, phone..."
					/>
				</div>
			</header>
			<main className="flex-1 overflow-y-auto p-6 pb-8 no-scrollbar">
				{loading ? (
					<div className="text-center py-12">
						<p className="text-white/60">Loading agents...</p>
					</div>
				) : (
					<div className="space-y-4">
						{filteredAgents.map((agent) => {
							const active = agent.user?.isActive !== false;
							return (
								<div
									key={agent.id}
									onClick={() => setSelectedAgent(agent)}
									className={`glass-card p-4 border-l-4 flex items-center gap-4 active:scale-[0.99] transition-transform cursor-pointer ${active ? 'border-[#00E676]' : 'border-white/20'}`}
								>
									<div className="flex-1 min-w-0">
										<p className="font-bold text-white">{getDisplayName(agent)}</p>
										<p className="text-xs text-white/60">{agent.user?.email}</p>
										{agent.user?.phone && <p className="text-xs text-white/50">{agent.user.phone}</p>}
										{!active && <span className="text-xs text-red-400 font-bold">Disabled</span>}
									</div>
									<ChevronRight className="w-5 h-5 text-white/40 shrink-0" />
								</div>
							);
						})}
						{filteredAgents.length === 0 && !loading && (
							<p className="text-center text-white/50 text-sm">No agents found</p>
						)}
					</div>
				)}
			</main>
		</div>
	);
};

export default AgentManagement;